import React, { useState, useRef, MouseEventHandler, ReactElement, useEffect, CSSProperties } from 'react';
import classNames from 'classnames';
import Button from '@cobalt/react-button';
import Icon, { Name } from '@cobalt/react-icon';
import Popup from '@cobalt/react-popup';
import { useTheme } from '@cobalt/react-theme-provider';
import usePosition, { CardinalPoint } from '@cobalt/react-position-hook';
import { Portal } from '@cobalt/react-portal-provider';

import './styles.scss';

const cls = 'dropdown';

interface DropdownProps {
  dataPrefix?: string;
  prefixCls?: string;
  align?: string;
  btnStyle?: CSSProperties;
  btnSize?: 'small' | 'medium';
  btnType?: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'feature' | 'mono-dark' | 'mono-light';
  btnVariation?: 'outline' | 'transparent';
  shape?: 'full-width' | 'compact';
  popupShape?: 'compact';
  disabled?: boolean;
  icon?: Name;
  origin?: CardinalPoint;
  direction?: CardinalPoint;
  gapX?: number;
  gapY?: number;
  autoGap?: number;
  children?: ReactElement;
  overlay?: ReactElement;
  withPortal?: boolean;
}

const withPortalReferToFlag = (component: JSX.Element, flag = false) =>
  flag ? <Portal>{component}</Portal> : component;

const Dropdown = (props: DropdownProps) => {
  const {
    dataPrefix,
    prefixCls,
    align,
    btnStyle,
    btnSize = 'small',
    btnType = 'secondary',
    btnVariation,
    shape,
    disabled,
    popupShape,
    icon,
    origin,
    direction,
    gapX,
    gapY,
    autoGap,
    overlay,
    children,
    withPortal = false,
  } = props;
  const [visible, setVisible] = useState(false);
  const theme = useTheme();
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [spreadDr, setSpreadDr] = useState<'up' | 'down'>();
  const popupStyleRef = useRef<ReturnType<typeof getPopupStyle>>();
  const componentCls = `${prefixCls}-${cls}`;

  const { position } = usePosition(btnRef.current, {
    origin: origin || 'SE',
    direction: direction || 'SW',
    gapX: gapX || 0,
    gapY: gapY || 0,
    autoGap: autoGap || 4,
  });

  const toggle: MouseEventHandler = (e) => {
    e.preventDefault();
    setVisible((prev) => !prev);
  };

  const getPopupStyle = () => {
    if (withPortal) {
      const btnClientRect = btnRef.current?.getBoundingClientRect();
      return spreadDr === 'up' && (direction || 'SW').indexOf('S') > -1
        ? {
            ...position,
            top: 'auto',
            left: btnClientRect?.left || 0,
            bottom: '-' + ((btnClientRect?.top || 0) - (gapY || autoGap || 4)) + 'px',
          }
        : {
            ...position,
            top: 'auto',
            left: btnClientRect?.left || 0,
            bottom:
              '-' +
              ((btnClientRect?.bottom || 0) + (popRef.current?.offsetHeight || 50) + (gapY || autoGap || 4)) +
              'px',
          };
    } else {
      return spreadDr === 'up' && (direction || 'SW').indexOf('S') > -1
        ? { ...position, top: 'auto', bottom: position?.top }
        : { ...position };
    }
  };

  if (typeof spreadDr !== 'undefined') {
    popupStyleRef.current = getPopupStyle();
  }

  useEffect(() => {
    if (!visible) return;
    const documentHeight = document.documentElement.clientHeight;
    const bottom = btnRef.current?.getBoundingClientRect().bottom || 0;
    const timer = setTimeout(() => {
      if (documentHeight - bottom < (popRef.current?.offsetHeight || 50)) {
        setSpreadDr('up');
      } else {
        setSpreadDr('down');
      }
    }, 0);

    return () => {
      setSpreadDr(undefined);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <div className={classNames(`${componentCls}-container`, align)}>
      <Button
        data-bi={dataPrefix}
        forwardedRef={btnRef}
        style={btnStyle}
        className={`${componentCls}-btnHover`}
        onClick={toggle}
        onBlur={() => setVisible(false)}
        size={btnSize}
        type={btnType}
        variation={btnVariation}
        shape={shape || 'compact'}
        disabled={disabled}
      >
        {children ? children : <Icon name={icon ? icon : 'more_horiz'} size="tiny" color={theme.gray800} />}
      </Button>

      {withPortalReferToFlag(
        <Popup
          forwardedRef={popRef}
          className={`${componentCls}-popup`}
          size="small"
          shape={popupShape}
          visible={visible && !!position}
          style={popupStyleRef.current}
        >
          {overlay || ''}
        </Popup>,
        withPortal,
      )}
    </div>
  );
};

Dropdown.defaultProps = {
  prefixCls: 'cobalt',
};

export default Dropdown;
